import React, { useState } from 'react';
import { styled } from '@mui/system';
import List from '@mui/material/List/index';
import ListItem from '@mui/material/ListItem/index';
import ListItemIcon from '@mui/material/ListItemIcon/index';
import ListItemText from '@mui/material/ListItemText/index';
import Divider from '@mui/material/Divider/index';
import Paper from '@mui/material/Paper/index';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import classNames from 'classnames';
import eventFormatter from '#component/device/event-formatter';
import eventIcon from '#component/icon/event';
import {
  AlertConfirmationDialog,
  ListConfirmationButton
} from '#component/device/overview/events/alert-confirmation';
import { getFormattedDate } from '#helper';

const StyledListItem = styled(ListItem)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.grey['200']
  }
}));

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: 35
}));

const StyledListItemText = styled(ListItemText)(() => ({
  '& .time': {
    fontSize: 12
  }
}));

const ListHeaderContainer = styled('div')(() => ({
  padding: '12px 0 12px 16px'
}));

const CloseListContainer = styled('div')(() => ({
  position: 'relative'
}));

const CloseListButton = styled(IconButton)(() => ({
  position: 'absolute',
  padding: 0,
  top: 0,
  right: 12,
  cursor: 'pointer',
  marginTop: 2
}));

const TitleList = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  textTransform: 'uppercase',
  marginRight: theme.spacing(4),
  overflow: 'hidden',
  textWrap: 'nowrap'
}));

const ListContainer = styled(Paper)(() => ({
  overflow: 'hidden auto',
  height: 'calc(100% - 46px)',
  '&::-webkit-scrollbar-track': {
    WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
    borderRadius: 10,
    backgroundColor: '#F5F5F5'
  },
  '&::-webkit-scrollbar': {
    width: 8,
    backgroundColor: '#F5F5F5'
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 10,
    WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,.3)',
    backgroundColor: '#a0a0a0'
  }
}));

const StyledDivider = styled(Divider)(() => ({
  marginTop: -1
}));

export default function EventsList(props) {
  const {
    events,
    handleEventClick,
    handleEventHover,
    closeList,
    account,
    syncSelectedDevicesWithStore,
    isMobile
  } = props;
  const dense = false;
  const [hoverEventId, setHoverEventId] = useState(null);
  const [confirmationPopupData, setConfirmationPopupData] = useState(null);
  const alertIdentifiers = ['shipment_40075', 'appliance_50055'];
  const alertConfirmAccess = account?.userType < 10 && account?.allowManageAlertAcceptance === 1;
  // re-rendered on `onClose` (EventsListDialog)
  if (!events.length) return null;

  const t2 = getFormattedDate(events[0].timestamp, account);
  const t1 = getFormattedDate(events[events.length - 1].timestamp, account);
  const title = t1 === t2 ? t1 : `${t1} – ${t2}`;

  const handleItemHover = (e, item) => {
    handleEventHover(e, item);

    if (e.type === 'mouseleave') {
      setHoverEventId(null);
    } else {
      setHoverEventId(item.details?.logId ?? null);
    }
  };

  const handleOpenConfirmationPopup = (e, item) => {
    e.stopPropagation();
    setConfirmationPopupData(item);
  };

  return (
    events && (
      <>
        <ListHeaderContainer>
          <CloseListContainer>
            <CloseListButton
              aria-label="Close"
              onClick={() => closeList([])}
            >
              <CloseRoundedIcon fontSize="small" />
            </CloseListButton>
          </CloseListContainer>
          <Tooltip title={title}>
            <TitleList>{title}</TitleList>
          </Tooltip>
        </ListHeaderContainer>
        <StyledDivider />
        <ListContainer className={classNames('events')} elevation={0}>
          <List dense={dense}>
            {events.map((item, i) => (
              <React.Fragment key={item.timestamp + i}>
                <StyledListItem
                  onClick={e => handleEventClick(e, item)}
                  onMouseEnter={e => handleItemHover(e, item)}
                  onMouseLeave={e => handleItemHover(e, item)}
                >
                  <StyledListItemIcon>
                    {eventIcon(item)}
                  </StyledListItemIcon>
                  <StyledListItemText>
                    {eventFormatter(item, account)}
                  </StyledListItemText>
                  {(item.details?.logId === hoverEventId || isMobile) && alertConfirmAccess &&
                    alertIdentifiers.includes(item.identifier) &&
                      <ListConfirmationButton
                        handleOpenConfirmationPopup={handleOpenConfirmationPopup}
                        item={item}
                        isMobile={isMobile}
                      />}
                </StyledListItem>
                {i < events.length - 1 ? <Divider /> : ''}
              </React.Fragment>
            ))}
          </List>
        </ListContainer>
        {confirmationPopupData &&
          <AlertConfirmationDialog
            isOpen={Boolean(confirmationPopupData)}
            setConfirmationPopupData={setConfirmationPopupData}
            item={confirmationPopupData}
            syncSelectedDevicesWithStore={syncSelectedDevicesWithStore}
          />}
      </>
    )
  );
}
